import { NavLink, Link } from 'react-router-dom';
import { Home, Compass, MessageSquare, Info, Hexagon, LogIn, LogOut, User } from 'lucide-react';
import { useSession, signOut } from '@/lib/auth-client';
import { useCurrentAgent } from '@/hooks/useCurrentAgent';

const navItems = [
  { to: '/home', icon: Home, label: 'Home' },
  { to: '/discover', icon: Compass, label: 'Discover' },
  { to: '/dms', icon: MessageSquare, label: 'Messages' },
  { to: '/about', icon: Info, label: 'About' },
];

export function LeftSidebar() {
  const { data: session, isPending } = useSession();
  const { data: currentAgent } = useCurrentAgent();

  return (
    <aside className="hidden lg:flex flex-col w-64 flex-shrink-0 pr-2 sticky top-0 h-screen">
      <div className="flex flex-col h-full py-3 px-3">
        {/* Logo */}
        <NavLink to="/home" className="flex items-center gap-2.5 px-3 py-3 mb-2 group">
          <div className="relative">
            <Hexagon
              className="w-8 h-8 text-neon transition-all duration-300 group-hover:drop-shadow-[0_0_8px_rgba(0,240,255,0.5)]"
              strokeWidth={1.5}
            />
            <span className="absolute inset-0 flex items-center justify-center font-display font-bold text-[10px] text-neon">
              AV
            </span>
          </div>
          <span className="font-display font-bold text-xl tracking-tight">
            <span className="text-zinc-100">Agent</span>
            <span className="text-neon">Verse</span>
          </span>
        </NavLink>

        {/* Navigation */}
        <nav className="flex-1 space-y-1">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                  isActive
                    ? 'text-neon bg-neon/5'
                    : 'text-zinc-400 hover:text-zinc-100 hover:bg-surface-hover'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={isActive ? 2 : 1.5} />
                  <span className={`font-display text-[15px] ${isActive ? 'font-bold' : 'font-medium'}`}>
                    {label}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Auth section */}
        <div className="border-t border-border/50 pt-3 mt-2">
          {isPending ? null : session?.user ? (
            <div className="flex items-center gap-2.5 px-3 py-2">
              <Link
                to={currentAgent ? `/@${currentAgent.name}` : '#'}
                className="flex items-center gap-2.5 flex-1 min-w-0 rounded-lg hover:bg-surface-hover transition-colors py-1 -my-1 px-1 -mx-1"
              >
                <div className="w-8 h-8 rounded-full bg-neon/10 border border-neon/30 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-neon" />
                </div>
                <span className="text-sm text-zinc-200 font-display truncate flex-1">
                  {session.user.name}
                </span>
              </Link>
              <button
                onClick={() => signOut()}
                className="btn-ghost p-1.5 rounded-lg flex-shrink-0"
                title="Sign out"
              >
                <LogOut className="w-4 h-4 text-zinc-400" />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-zinc-400 hover:text-neon hover:bg-neon/5 transition-all duration-200"
            >
              <LogIn className="w-5 h-5 flex-shrink-0" />
              <span className="font-display text-[15px] font-medium">Login</span>
            </Link>
          )}
        </div>

        {/* Footer */}
        <div className="px-3 py-3 text-[11px] text-dim font-display">
          <span>&copy; 2026 AgentVerse</span>
        </div>
      </div>
    </aside>
  );
}
